// src/components/widgets/MobileSafetySignalLight.jsx
// 모바일 전용 안전 신호등 컴포넌트
import React, { useEffect, useState, useMemo } from "react";
import { getSafetyDashboard } from "../../api/safety";

export default function MobileSafetySignalLight({ onLightClick }) {
  const projectId = useMemo(() => localStorage.getItem("projectId"), []);
  const [data, setData] = useState({
    agg: { green: 0, yellow: 0, red: 0 },
    monthly: [],
  });

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res = await getSafetyDashboard({ project: projectId });
        setData(res || {});
      } catch (err) {
        console.error("안전 지표 불러오기 실패:", err);
      }
    })();
  }, [projectId]);

  const { green, yellow, red } = data.agg || {};
  const total = green + yellow + red;

  // 🔥 새로운 안전 지표 계산식
  // 1. 적합률 = (양호 + 조치중) / 전체 × 100% 
  //    → 주의(red)만 문제로 간주, 나머지는 모두 적합
  const complianceRate = total ? Math.round(((green + yellow) / total) * 100) : 0;

  // 2. 위험률 = 주의 / 전체 × 100%
  //    → 실제 위험한 항목의 비율
  const riskRate = total ? Math.round((red / total) * 100) : 0;

  // 3. 완료율 = 양호 / 전체 × 100%
  //    → 완전히 처리 완료된 항목의 비율
  const completionRate = total ? Math.round((green / total) * 100) : 0;

  const COLORS = {
    green: "#22c55e",
    yellow: "#eab308",
    red: "#ef4444",
  };

  const circles = [
    { label: "양호", value: green, color: COLORS.green, status: "green" },
    { label: "조치 중", value: yellow, color: COLORS.yellow, status: "yellow" },
    { label: "주의", value: red, color: COLORS.red, status: "red" },
  ];

  return (
    <div className="flex flex-col items-center gap-2.5 flex-shrink-0 min-w-0 w-full" style={{ minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* 양호/조치중/주의 원형 버튼들 */}
      <div className="flex items-center justify-center gap-2 w-full">
        {circles.map((c) => (
          <button
            key={c.label}
            onClick={() => onLightClick && onLightClick(c.status)}
            className="flex flex-col items-center justify-center rounded-full border-2 text-center transition-all active:scale-95"
            style={{
              borderColor: c.color,
              color: c.color,
              width: '3rem',
              height: '3rem',
              minWidth: '3rem',
              minHeight: '3rem',
              fontSize: '0.625rem',
              lineHeight: '0.875rem',
            }}
          >
            <p className="font-semibold leading-tight" style={{ fontSize: '0.625rem', lineHeight: '0.75rem' }}>
              {c.label}
            </p>
            <p className="font-bold mt-0.5" style={{ fontSize: '0.75rem', lineHeight: '1rem' }}>{c.value}</p>
          </button>
        ))}
      </div>

      {/* 🔥 새로운 안전 지표 - 3단 표시 */}
      <div className="w-full grid grid-cols-3 gap-1.5 text-center">
        {/* 적합률 (메인 지표) */}
        <div className="bg-blue-50 rounded-lg px-2 py-1.5 border border-blue-200">
          <p className="text-[9px] text-blue-600 font-medium leading-tight">적합률</p>
          <p className="text-sm font-bold text-blue-700 leading-tight mt-0.5">{complianceRate}%</p>
        </div>

        {/* 위험률 */}
        <div className="bg-red-50 rounded-lg px-2 py-1.5 border border-red-200">
          <p className="text-[9px] text-red-600 font-medium leading-tight">위험률</p>
          <p className="text-sm font-bold text-red-700 leading-tight mt-0.5">{riskRate}%</p>
        </div>

        {/* 완료율 */}
        <div className="bg-green-50 rounded-lg px-2 py-1.5 border border-green-200">
          <p className="text-[9px] text-green-600 font-medium leading-tight">완료율</p>
          <p className="text-sm font-bold text-green-700 leading-tight mt-0.5">{completionRate}%</p>
        </div>
      </div>

      {/* 총 건수 */}
      <div className="text-center w-full">
        <p className="text-[10px] text-slate-500" style={{ fontSize: '0.625rem', lineHeight: '0.875rem' }}>
          총 <span className="font-semibold text-slate-700">{total}</span>건
        </p>
      </div>
    </div>
  );
}
